'use client';

import { useTranslations } from 'next-intl';
import { type FC, useMemo } from 'react';
import { TASK_SETTING_OPTION } from '@/app/constants';
import { ToolSettingsWithConfig } from '../../_components/ToolSettingsWithConfig';

type ScenarioType = 'unspecified' | 'general' | 'ecommerce_ad';

interface OneClickVideoProductionSettingsProps {
  scenario?: ScenarioType;
  aspectRatio?: string;
  duration?: number;
  voiceLang?: string;
  onScenarioChange: (value: ScenarioType) => void;
  onAspectRatioChange: (value: string) => void;
  onDurationChange: (value: number) => void;
  onVoiceLangChange: (value: string) => void;
}

const ASPECT_RATIO_OPTIONS = ['1:1', '16:9', '9:16', '4:3', '3:4'];

const DURATION_OPTIONS = [10, 15, 20, 30, 45, 60];

export const SETTING_KEY_CONFIG: TASK_SETTING_OPTION[] = [
  TASK_SETTING_OPTION.Scenario,
  TASK_SETTING_OPTION.AspectRatio,
  TASK_SETTING_OPTION.Duration,
];

export const OneClickVideoProductionSettings: FC<OneClickVideoProductionSettingsProps> = ({
  scenario = 'general',
  aspectRatio = '16:9',
  duration = 15,
  voiceLang,
  onScenarioChange,
  onAspectRatioChange,
  onDurationChange,
  onVoiceLangChange,
}) => {
  const t = useTranslations();
  const scenarios = [
    {
      value: 'general' as const,
      label: t('tools.oneClickVideoProduction.scenario.general.label'),
      description: t('tools.oneClickVideoProduction.scenario.general.description'),
      showVip: false,
    },
    {
      value: 'ecommerce_ad' as const,
      label: t('tools.oneClickVideoProduction.scenario.ecommerce_ad.label'),
      description: t('tools.oneClickVideoProduction.scenario.ecommerce_ad.description'),
      showVip: false,
    },
  ];
  const aspectRatioOptions = ASPECT_RATIO_OPTIONS.map((ratio) => ({ label: ratio, value: ratio, showVip: false }));
  const durationOptions = DURATION_OPTIONS.map((option) => ({
    label: option.toString(),
    value: option.toString(),
    showVip: false,
  }));
  const voiceLangOptions = [
    { value: 'zh', label: t('tools.oneClickVideoProduction.lang.zh'), showVip: false },
    { value: 'en', label: t('tools.oneClickVideoProduction.lang.en'), showVip: false },
  ];

  const includedOptions = useMemo(() => {
    return [...SETTING_KEY_CONFIG, ...(scenario !== 'general' ? [TASK_SETTING_OPTION.VoiceLanguage] : [])];
  }, [scenario]);

  return (
    <ToolSettingsWithConfig
      className="w-full"
      includedOptions={includedOptions}
      showOptionDivider={false}
      optionsWrapperClassName="gap-5 md:gap-5"
      scenarioOption={{
        value: scenario,
        options: scenarios,
        onChange: (value) => onScenarioChange(value as ScenarioType),
      }}
      voiceLanguageOption={{
        value: voiceLang,
        options: voiceLangOptions,
        onChange: onVoiceLangChange,
        title: t('tools.oneClickVideoProduction.lang.label'),
      }}
      aspectRatioOption={{
        value: aspectRatio,
        options: aspectRatioOptions,
        onChange: onAspectRatioChange,
        title: t('tools.oneClickVideoProduction.aspectRatio.label'),
      }}
      durationOption={{
        value: duration,
        options: durationOptions,
        onChange: (val) => onDurationChange(Number(val)),
        title: t('tools.oneClickVideoProduction.duration.label'),
      }}
    />
  );
};
